import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

export default function MemberProfileOverview() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/"); return; }
    if (user.role === "admin") { navigate("/admin"); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/profile");
        setProfile(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  if (!user || user === false) return null;

  const svcEntries = Object.entries(profile?.trip_services || {}).filter(([, v]) => v > 0);

  return (
    <div
      data-testid="profile-overview-page"
      className="min-h-screen text-neutral-200"
      style={{
        background: "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 45%, #050506 85%, #000000 100%)",
        fontFamily: "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <header className="sticky top-0 z-30 backdrop-blur-md" style={{ borderBottom: "1px solid #22252A", background: "rgba(0,0,0,0.72)" }}>
        <div className="mx-auto max-w-4xl px-5 py-4 flex items-center gap-4">
          <Link to="/member" className="flex items-center gap-3 min-w-0">
            <div>
              <div aria-hidden className="w-40 h-4"
                style={{
                  background: METAL_BG,
                  WebkitMaskImage: "url(/wellnet-logo.png)", maskImage: "url(/wellnet-logo.png)",
                  WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                  WebkitMaskSize: "contain", maskSize: "contain",
                  WebkitMaskPosition: "left center", maskPosition: "left center",
                }}
              />
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mt-1">Profile</div>
            </div>
          </Link>
          <div className="ml-auto flex items-center gap-4 text-[10px] uppercase tracking-[0.25em]">
            <span className="text-neutral-400 hidden sm:inline">{user.email}</span>
            <button
              data-testid="overview-signout"
              onClick={async () => { await logout(); navigate("/"); }}
              className="border border-neutral-600 text-neutral-300 px-3 py-1.5 hover:bg-neutral-200 hover:text-black transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pt-10 pb-20">
        <Link
          to="/member"
          data-testid="overview-back"
          className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.3em] text-neutral-300 hover:text-neutral-100 hover:border-neutral-200 transition-colors"
          style={{ border: "1px solid #31353C", borderRadius: 8 }}
        >
          <span aria-hidden>←</span> Back
        </Link>
        <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1
              className="font-semibold tracking-tight text-neutral-100"
              style={{
                fontFamily: "'Syne','Archivo',sans-serif",
                fontSize: "clamp(29px,4.6vw,44px)",
                lineHeight: 1.1,
              }}
            >
              Your profile
            </h1>
            <p className="mt-3 max-w-[60ch] text-neutral-400 text-[15.5px]">
              Everything we have on file. Tap edit to change anything.
            </p>
          </div>
          <Link
            to="/member/profile/edit"
            data-testid="overview-edit-btn"
            className="px-5 py-2.5 text-sm font-bold"
            style={{ background: METAL_BG, color: "#08090B", borderRadius: 8 }}
          >
            Edit profile
          </Link>
        </div>

        {loading ? (
          <div className="mt-12 text-xs uppercase tracking-[0.3em] text-neutral-500">Loading…</div>
        ) : error ? (
          <div className="mt-6 text-xs uppercase tracking-[0.2em] px-3 py-2" style={{ color: "#F1928A", border: "1px solid #F1928A" }}>{error}</div>
        ) : (
          <div className="mt-10 flex flex-col gap-6">
            <Block title="You">
              <KV label="Name" value={profile.name || user.name || user.email.split("@")[0]} />
              <KV label="Email" value={profile.email || user.email} />
              <KV label="Phone" value={profile.phone} />
              <KV label="Home city" value={profile.home_city} />
            </Block>

            <Block title="Cities you travel to">
              {profile.cities?.length ? (
                <ol className="space-y-2 list-decimal list-inside">
                  {profile.cities.map((c) => (
                    <li key={c} className="text-neutral-100 text-[14.5px]">{c}</li>
                  ))}
                </ol>
              ) : <Empty text="No cities added." />}
            </Block>

            <Block title="How you travel">
              <KV label="How often" value={profile.frequency} />
              <KV label="Typical trip" value={profile.nights ? `${profile.nights} night${profile.nights === 1 ? "" : "s"}` : ""} />
              <KV label="Usual notice" value={profile.notice} />
              <KV label="Travels with" value={profile.companions} />
              <KV label="Preferred areas" value={profile.areas} />
              <KV label="Where you stay" value={profile.accommodation} />
            </Block>

            <Block title="What a normal trip needs">
              {svcEntries.length ? (
                <div className="flex flex-wrap gap-2">
                  {svcEntries.map(([svc, qty]) => (
                    <span
                      key={svc}
                      className="px-3 py-1.5 text-[13px] text-neutral-100 rounded-full"
                      style={{ border: "1px solid #31353C", background: "#0B0C0F" }}
                    >
                      {svc} <span className="text-neutral-500 tabular-nums">× {qty}</span>
                    </span>
                  ))}
                </div>
              ) : <Empty text="Nothing set yet." />}
            </Block>

            <Block title="When it suits you">
              {profile.times?.length ? <ChipList items={profile.times} /> : <Empty text="No times picked." />}
            </Block>

            <Block title="What you're after">
              {profile.goals?.length ? <ChipList items={profile.goals} /> : <Empty text="No goals set." />}
            </Block>

            <div className="text-sm text-neutral-500 italic">
              Health intake lives in a separate form, screened once and kept apart from everything here.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="rounded-xl p-5 sm:p-6" style={{ background: "#101114", border: "1px solid #22252A" }}>
      <h2 className="text-[16.5px] font-semibold tracking-[-0.005em] text-neutral-100 mb-4" style={{ fontFamily: "'Syne','Archivo',sans-serif" }}>{title}</h2>
      <div className="flex flex-col gap-2.5">{children}</div>
    </section>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex gap-4 py-1.5" style={{ borderBottom: "1px solid #1a1c20" }}>
      <dt className="w-[130px] flex-none text-[11px] font-semibold tracking-[0.13em] uppercase text-neutral-500 pt-0.5">{label}</dt>
      <dd className={`flex-1 min-w-0 text-[14.5px] ${value ? "text-neutral-100" : "text-neutral-600 italic"}`}>
        {value || "Not set"}
      </dd>
    </div>
  );
}

function ChipList({ items }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((label) => (
        <span key={label} className="px-3 py-1.5 text-[13px] text-neutral-100 rounded-full" style={{ border: "1px solid #31353C", background: "#0B0C0F" }}>
          {label}
        </span>
      ))}
    </div>
  );
}

function Empty({ text }) { return <div className="text-sm text-neutral-500 italic">{text}</div>; }
